import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Notification,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { showNotification } from '@mantine/notifications'
import { Campaign } from '@ospace/schemas'
import { IconDownload } from '@tabler/icons-react'
import { yupResolver } from 'mantine-form-yup-resolver'
import * as Yup from 'yup'

import { useExportLeads, useHubspotUsers, useLemlistCampaigns } from '../hooks'
import { LeadExportProgress } from './LeadExportProgress'

export const LeadExport = ({ campaign }: { campaign: Campaign }) => {
  const hubspotOwners = useHubspotUsers()
  const lemlistCampaignsQuery = useLemlistCampaigns(campaign.id)
  const exportLeadsMutation = useExportLeads()

  const validationSchema = Yup.object().shape({
    hubspotOwnerId: Yup.string().required('LDR is required'),
    exclusionListId: Yup.string().required('Exclusion ID is required'),
    lemlistCampaignId: Yup.string().required('Lemlist Campaign is required'),
  })
  //repsonse after export {"exportId":"export-1762334601545","campaignId":"62","ldrName":"Lousise asdasd","total":2,"message":"Export to Lemlist triggered for 2 leads."}
  const form = useForm({
    initialValues: {
      hubspotOwnerId: '',
      exclusionListId: '',
      lemlistCampaignId: '',
      hasLeads: false,
    },
    validate: yupResolver(validationSchema),
  })

  const handleExport = async (values: typeof form.values) => {
    try {
      await exportLeadsMutation.mutateAsync({
        campaignId: campaign.id,
        hubspotOwnerId: values.hubspotOwnerId,
        exclusionListId: values.exclusionListId,
        lemlistCampaignId: values.lemlistCampaignId,
        hasLeads: values.hasLeads,
      })
    } catch (error) {
      showNotification({
        title: 'Export Failed',
        message: 'There was an error exporting the leads.',
        color: 'red',
      })
      console.error('Export error:', error)
    }
  }

  const uniqueLDRs: string[] = [
    ...new Set((hubspotOwners.data ?? []).map((o: any) => o.name)),
  ] as string[]

  return (
    <Box>
      <Title order={2} mb='md'>
        Export Contacts
      </Title>
      <form onSubmit={form.onSubmit(handleExport)}>
        <Stack gap='md'>
          <Grid gutter='md'>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label='Select LDR'
                m={'md'}
                placeholder='Choose an LDR'
                data={uniqueLDRs.map((name) => ({ value: name, label: name }))}
                {...form.getInputProps('hubspotOwnerId')}
                required
                searchable
              />
              <TextInput
                m={'md'}
                label='Exclusion ID'
                placeholder='Enter exclusion ID'
                {...form.getInputProps('exclusionListId')}
                required={true}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label='Lemlist Campaign'
                m={'md'}
                placeholder='Choose a Lemlist Campaign'
                required={true}
                data={
                  lemlistCampaignsQuery.data
                    ? lemlistCampaignsQuery.data.map((lc: any) => ({
                        value: lc.id,
                        label: lc.name,
                      }))
                    : []
                }
                {...form.getInputProps('lemlistCampaignId')}
              />
              <Switch m={'md'} label='Has Leads' {...form.getInputProps('hasLeads')} />
            </Grid.Col>
          </Grid>

          <Flex justify='flex-end' mt='md'>
            <Button
              type='submit'
              leftIcon={<IconDownload size={16} />}
              loading={exportLeadsMutation.isLoading}
            >
              Start Export
            </Button>
          </Flex>
        </Stack>
      </form>

      {exportLeadsMutation.isSuccess && (
        <Notification mt='md' color='teal' title='Export Started' withCloseButton={false}>
          Export has been started successfully. You can monitor the progress below.
        </Notification>
      )}

      {exportLeadsMutation.isError && (
        <Notification mt='md' color='red' title='Export Failed' withCloseButton={false}>
          There was an error exporting the leads.
        </Notification>
      )}

      {exportLeadsMutation.data?.exportId && (
        <Card mt='md' withBorder>
          <Text>Export Progress:</Text>
          <LeadExportProgress
            exportId={exportLeadsMutation.data.exportId}
            campaignId={campaign.id}
          />
        </Card>
      )}
    </Box>
  )
}

export default LeadExport
